
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ProductTypes } from "@/data/enums/products";

// Types
import { IState } from "@/store";
import {
  IUpgradeDowngradeProvider,
  IUpgradeDowngradeProviderMethods
} from "@/models/providers/upgradeDowngrades";
import { IProduct, IProductCategory } from "@/models/products";
import { IContract, IContractProduct } from "@/models/contracts";
import { IInvoice } from "@/models/invoices";

@Component({
  computed: {
    ...mapState({
      migrationProductScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](
          this.migrationProductId
        );
      },
      migrationProduct(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.migrationProductScope
        });
      },
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CATALOGUE_CATEGORIES,
          scope: this.rootCategoriesScope
        });
      },
      rootCategoriesScope(state: IState, getters) {
        return getters[
          `data/${DataModules.CATALOGUE_CATEGORIES}/rootCategoriesScope`
        ](this.contractBrandId, this.productTypes);
      }
    })
  }
})
export default class UpgradeDowngradeProvider extends Mixins(RequestPromises) {
  // Map state

  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];
  public migrationProduct!: IProduct;
  public migrationProductScope!: string;

  // Props

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ required: true })
  public contractProduct!: IContractProduct & { product: IProduct };

  @Prop({ required: true })
  public contract!: IContract;

  @Prop({ required: true })
  public migrationProductId!: IProduct["id"];

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  public productTypes: ProductTypes[] = [
    ProductTypes.SINGLE_PRODUCT,
    ProductTypes.PRODUCT_BUNDLE
  ];

  public charLimit = 150;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.migrationProductScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CATALOGUE_CATEGORIES,
      scope: this.rootCategoriesScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([this.getProductData(), this.getRootCategories()]);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getProductData() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.migrationProductId,
      scope: this.migrationProductScope,
      isBasketContext: true,
      params: {
        currency_id: this.contract.currency_id,
        omit_promotions: 1
      },
      vm: this,
      ignoreStored: this.isReloading
    });
  }

  public getRootCategories() {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/list`,
      {
        productTypes: this.productTypes,
        scope: this.rootCategoriesScope,
        params: {
          order: "order",
          limit: 0,
          "filter[level]": 1,
          brand_id: this.contractBrandId
        },
        vm: this,
        ignoreStored: this.isReloading
      }
    );
  }

  public changeProduct(data): Promise<IInvoice> {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS_PRODUCTS}/changeProduct`,
      {
        contractId: this.contract.id,
        contractProductId: this.contractProduct.id,
        data
      }
    );
  }

  public getFullProductPath(categories, categoryId: IProductCategory["id"]) {
    let path = [];

    function dive(_data, _path, _, level) {
      for (let i = 0; i < _data.length; i++) {
        if (_data[i].id === categoryId) {
          path = _.concat(_path, [
            { id: _data[i].id, name: _data[i].name, order: level }
          ]);
        } else if (_.has(_data[i], "subcategories")) {
          dive(
            _data[i].subcategories,
            _.concat(_path, [
              { id: _data[i].id, name: _data[i].name, order: level }
            ]),
            _,
            level + 1
          );
        }
      }
    }

    dive(categories, [], this.$_, 0);

    return path;
  }

  // Computed

  public get contractBrandId() {
    return this.contract.brand_id;
  }

  public get parentCategories() {
    return this.getFullProductPath(
      this.rootCategories,
      this.$_.get(this.migrationProduct, "category_id")
    );
  }

  // Guarding

  public guardFunctionalities = ["reports_help"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provider

  @Provide("upgradeDowngradeProvider")
  public providerData(): IUpgradeDowngradeProvider {
    return {
      contract: this.contract,
      contractProduct: this.contractProduct,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      migrationProduct: this.migrationProduct,
      parentCategories: this.parentCategories
    };
  }

  @Provide("upgradeDowngradeProviderMethods")
  public get providerMethods(): IUpgradeDowngradeProviderMethods {
    return {
      changeProduct: this.changeProduct,
      reloadData: this.reloadData
    };
  }

  // Render Function

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $upgradeDowngradeData: this.providerData(),
        $upgradeDowngradeMethods: this.providerMethods
      })
    );
  }
}
